import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Keywords and their target paths and slugs
const keywords = [
  { text: 'Støbearbejde', path: '/ydelser/stoebearbejde', slug: 'stoebearbejde' },
  { text: 'Ny indkørsel', path: '/ydelser/belaegning/ny-indkoersel', slug: 'ny-indkoersel' },
  { text: 'Haveanlæg', path: '/ydelser/anlaegsarbejde/haveanlaeg', slug: 'haveanlaeg' },
  { text: 'Støttemur', path: '/ydelser/belaegning/stoettemur', slug: 'stoettemur' },
  { text: 'Ny terrasse', path: '/ydelser/belaegning/ny-terrasse', slug: 'ny-terrasse' },
  { text: 'Belægning', path: '/ydelser/belaegning', slug: 'belaegning' },
  { text: 'Entreprenør', path: '/ydelser/entreprenoer', slug: 'entreprenoer' },
  { text: 'Betonarbejde', path: '/ydelser/betonarbejde', slug: 'betonarbejde' },
  { text: 'Murerarbejde', path: '/ydelser/murerarbejde', slug: 'murerarbejde' },
];

function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function buildKeywordRegex(keywordText) {
  const escaped = escapeRegExp(keywordText);
  // Match boundary at start (either start of string or a non-letter)
  // Match keyword
  // Match optional Danish suffix: s, e, er, en, et, ene, erne
  // Match boundary at end (either end of string or a non-letter)
  return new RegExp(`(^|[^a-zA-ZæøåÆØÅ])(${escaped})(s|e|er|en|et|ene|erne)?(?![a-zA-ZæøåÆØÅ])`, 'i');
}

function findMatch(text, keywordText) {
  const regex = buildKeywordRegex(keywordText);
  const match = regex.exec(text);
  if (!match) return null;

  const prefix = match[1] || '';
  const keywordMatch = match[2];
  const suffix = match[3] || '';

  const matchIndex = match.index + prefix.length;
  const matchLength = keywordMatch.length + suffix.length;
  const matchedText = keywordMatch + suffix;

  return {
    index: matchIndex,
    length: matchLength,
    text: matchedText
  };
}

function processTextNode(node, currentSlug, linkedKeywords, state) {
  const text = node.nodeValue;
  const candidates = [];

  // Add Larsen & Kristensen if not already linked and not on homepage
  if (!state.linkedLarsen && currentSlug !== 'forside' && currentSlug !== 'larsen-kristensen') {
    candidates.push({ text: 'Larsen & Kristensen', path: '/', key: 'larsen-kristensen' });
  }

  // Add other keywords if not already linked and not on their own page
  keywords.forEach(kw => {
    if (!linkedKeywords.has(kw.text) && kw.slug !== currentSlug) {
      candidates.push({ text: kw.text, path: kw.path, key: kw.text });
    }
  });

  // Sort candidates by text length descending to match longer phrases first
  candidates.sort((a, b) => b.text.length - a.text.length);

  if (candidates.length === 0) return;

  let earliestMatch = null;
  let earliestMatchDetails = null;

  candidates.forEach(cand => {
    const details = findMatch(text, cand.text);
    if (details) {
      if (!earliestMatchDetails || details.index < earliestMatchDetails.index) {
        earliestMatchDetails = details;
        earliestMatch = cand;
      }
    }
  });

  if (earliestMatch && earliestMatchDetails) {
    if (earliestMatch.key === 'larsen-kristensen') {
      state.linkedLarsen = true;
    } else {
      linkedKeywords.add(earliestMatch.key);
    }

    const beforeText = text.substring(0, earliestMatchDetails.index);
    const matchText = earliestMatchDetails.text;
    const afterText = text.substring(earliestMatchDetails.index + earliestMatchDetails.length);

    const fragment = document.createDocumentFragment();
    if (beforeText) {
      fragment.appendChild(document.createTextNode(beforeText));
    }

    const link = document.createElement('a');
    link.href = earliestMatch.path;
    link.className = 'text-[#163A2B] font-semibold hover:text-[#1C1E1D] transition-colors';
    link.textContent = matchText;
    fragment.appendChild(link);

    if (afterText) {
      const afterNode = document.createTextNode(afterText);
      fragment.appendChild(afterNode);
      processTextNode(afterNode, currentSlug, linkedKeywords, state);
    }

    node.parentNode.replaceChild(fragment, node);
  }
}

export function injectInternalLinks(html, currentSlug) {
  if (!html) return '';

  // In non-browser environments (like SSR/testing), return original HTML
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return html;
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(`<body>${html}</body>`, 'text/html');
    const body = doc.body;

    const linkedKeywords = new Set();
    const state = { linkedLarsen: false };

    // Find all text nodes that are not inside an <a> tag
    const textNodes = [];
    const walk = doc.createTreeWalker(body, NodeFilter.SHOW_TEXT, null, false);
    let n;
    while (n = walk.nextNode()) {
      textNodes.push(n);
    }

    for (const node of textNodes) {
      if (node.parentElement && node.parentElement.closest('a')) {
        continue;
      }
      processTextNode(node, currentSlug, linkedKeywords, state);
    }

    return body.innerHTML;
  } catch (error) {
    console.error('Error injecting internal links:', error);
    return html;
  }
}
